import React, { useState, useEffect } from "react";
import { api, apiErr } from "../../lib/api";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, ShieldCheck, ShieldOff, Loader2, MailCheck } from "lucide-react";

function PwdField({ label, value, onChange, show, onToggle, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-ink-600 mb-1 block">{label}</span>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          data-testid={testid}
          className="w-full rounded-lg border border-ink-400/25 px-3 py-2 pr-10 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
        />
        <button type="button" onClick={onToggle} className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-600" tabIndex={-1}>
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </label>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (next.length < 8) return toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères.");
    if (next !== confirm) return toast.error("Les mots de passe ne correspondent pas.");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Mot de passe modifié avec succès.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6 max-w-md" data-testid="security-tab">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center"><KeyRound size={20} className="text-brand" strokeWidth={1.5} /></div>
        <div>
          <p className="font-heading text-lg text-ink-900">Changer le mot de passe</p>
          <p className="text-xs text-ink-400">Sécurisez l'accès à votre administration.</p>
        </div>
      </div>
      <div className="space-y-4">
        <PwdField label="Mot de passe actuel" value={current} onChange={(e) => setCurrent(e.target.value)} show={show} onToggle={() => setShow((s) => !s)} testid="current-password" />
        <PwdField label="Nouveau mot de passe (min. 8 caractères)" value={next} onChange={(e) => setNext(e.target.value)} show={show} onToggle={() => setShow((s) => !s)} testid="new-password" />
        <PwdField label="Confirmer le nouveau mot de passe" value={confirm} onChange={(e) => setConfirm(e.target.value)} show={show} onToggle={() => setShow((s) => !s)} testid="confirm-password" />
        <button onClick={submit} disabled={saving || !current || !next || !confirm} className="w-full rounded-full bg-brand text-white py-3 font-medium hover:bg-brand-600 disabled:opacity-50" data-testid="change-password-submit">
          {saving ? "Modification…" : "Modifier le mot de passe"}
        </button>
      </div>
    </div>
  );
}

function TwoFactorCard() {
  const [enabled, setEnabled] = useState(null);
  const [email, setEmail] = useState("");
  const [setup, setSetup] = useState(false); // code sent, awaiting confirmation
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [pwd, setPwd] = useState("");
  const [disabling, setDisabling] = useState(false);

  const load = () => api.get("/auth/2fa/status")
    .then((r) => { setEnabled(r.data.enabled); setEmail(r.data.email || ""); })
    .catch(() => setEnabled(false));
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try { const r = await api.post("/auth/2fa/setup"); setSetup(true); if (r.data.sent_to) setEmail(r.data.sent_to); toast.success("Code envoyé par e-mail."); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const confirmEnable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code: code.trim() });
      toast.success("Double authentification activée ✅");
      setSetup(false); setCode(""); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: pwd });
      toast.success("Double authentification désactivée.");
      setPwd(""); setDisabling(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const inp = "w-full rounded-lg border border-ink-400/25 px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand";

  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6 max-w-md" data-testid="twofa-card">
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${enabled ? "bg-emerald-50" : "bg-brand-50"}`}>
          <ShieldCheck size={20} className={enabled ? "text-emerald-600" : "text-brand"} strokeWidth={1.5} />
        </div>
        <div>
          <p className="font-heading text-lg text-ink-900">Double authentification (2FA)</p>
          <p className="text-xs text-ink-400">
            {enabled === null ? "Chargement…" : enabled
              ? <>Activée — un code envoyé à <b className="text-ink-600">{email}</b> est demandé à chaque connexion.</>
              : <>Recevez un code par e-mail{email ? <> à <b className="text-ink-600">{email}</b></> : ""} à chaque connexion pour sécuriser votre accès.</>}
          </p>
        </div>
      </div>

      {enabled === false && !setup && (
        <button onClick={startSetup} disabled={busy} className="w-full rounded-full bg-brand text-white py-3 font-medium hover:bg-brand-600 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="twofa-activate">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <MailCheck size={16} />} Activer la double authentification
        </button>
      )}

      {enabled === false && setup && (
        <div className="space-y-4" data-testid="twofa-setup">
          <p className="text-sm text-ink-600">Nous avons envoyé un code à 6 chiffres à <b>{email}</b>. Saisissez-le ci-dessous pour activer la double authentification :</p>
          <input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" className={`${inp} text-center tracking-[0.3em] text-lg`} data-testid="twofa-confirm-code" />
          <div className="flex gap-2">
            <button onClick={confirmEnable} disabled={busy || code.length < 6} className="flex-1 rounded-full bg-brand text-white py-2.5 font-medium hover:bg-brand-600 disabled:opacity-50" data-testid="twofa-confirm">Confirmer</button>
            <button onClick={() => { setSetup(false); setCode(""); }} className="rounded-full border border-ink-400/30 text-ink-600 px-5 py-2.5">Annuler</button>
          </div>
          <button onClick={startSetup} disabled={busy} className="w-full text-xs text-ink-400 hover:text-brand transition-colors" data-testid="twofa-resend">Renvoyer le code</button>
        </div>
      )}

      {enabled === true && !disabling && (
        <button onClick={() => setDisabling(true)} className="w-full rounded-full border border-red-200 text-red-600 py-3 font-medium hover:bg-red-50 inline-flex items-center justify-center gap-2" data-testid="twofa-disable-start">
          <ShieldOff size={16} /> Désactiver la double authentification
        </button>
      )}

      {enabled === true && disabling && (
        <div className="space-y-3" data-testid="twofa-disable">
          <p className="text-sm text-ink-600">Confirmez avec votre mot de passe pour désactiver la 2FA :</p>
          <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="Mot de passe" className={inp} data-testid="twofa-disable-pwd" />
          <div className="flex gap-2">
            <button onClick={disable} disabled={busy || !pwd} className="flex-1 rounded-full bg-red-600 text-white py-2.5 font-medium hover:bg-red-700 disabled:opacity-50" data-testid="twofa-disable-confirm">Désactiver</button>
            <button onClick={() => { setDisabling(false); setPwd(""); }} className="rounded-full border border-ink-400/30 text-ink-600 px-5 py-2.5">Annuler</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SecurityTab() {
  return (
    <div className="space-y-6">
      <PasswordCard />
      <TwoFactorCard />
    </div>
  );
}
